import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bike,
  Check,
  ChefHat,
  Clock,
  Copy,
  MapPin,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import type { DevolucionPendienteVista, DomicilioEnTableroVista, PedidoVista } from '@shared/types';
import {
  CLAVE_DATOS_PAGO,
  CLAVE_MINUTOS_ESPERA_PAGO,
  COLUMNAS_TABLERO,
  ETIQUETA_MOTIVO,
  esperaDePagoVencida,
  leerMinutosEspera,
  minutosEsperando,
  type MotivoEspera,
} from '@shared/domicilioEspera';
import {
  mensajeAcuse,
  mensajeDatosParaPagar,
  mensajeRecordatorioPago,
} from '@shared/mensajesWhatsapp';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { copiarAlPortapapeles } from '@renderer/lib/portapapeles';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { seLePuedeCobrar } from '@shared/cobroVisible';
import { calcularCobroEnLaPuerta } from '@shared/cobroEnLaPuerta';
import { puedeUsarCanal } from '@shared/permisos';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { CobroTransferenciaModal } from '@renderer/components/cobro/CobroTransferenciaModal';
import { AsignarRepartidorModal } from '@renderer/components/despacho/AsignarRepartidorModal';
import { NoEntregadoModal } from '@renderer/components/despacho/NoEntregadoModal';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirDocumento } from '@renderer/lib/impresionNavegador';
import { ComoEmpiezaElPedido } from '@renderer/components/domicilios/ComoEmpiezaElPedido';
import { CotizarEnvioModal } from '@renderer/components/pos/CotizarEnvioModal';

/**
 * Sprint 76 — El tablero de domicilios.
 *
 * La pantalla que se queda abierta todo el día, junto a WhatsApp Web.
 *
 * Nace de mirar el flujo real del negocio: el cliente escribe, la dueña pide ubicación, pregunta
 * el precio del envío en un grupo, pasa el total, espera la transferencia, manda a cocina,
 * entrega a la moto y espera el acuse. **Tres de esas esperas no dependen de ella**, y el
 * asistente de cinco pasos las trataba como si no existieran: se abría, se llenaba y no se podía
 * soltar. Con dos clientes escribiendo a la vez había que terminar uno para empezar el otro.
 *
 * Aquí cada pedido dice **qué espera y desde hace cuántos minutos**, y ofrece **una** acción. El
 * reloj es lo único que hace que alguien levante el teléfono; "paso 3 de 5" no le sirve a nadie.
 */
export function DomiciliosPage() {
  /* Sprint 111 — `navigate` quedó sin uso: esta pantalla dejó de mandar a otra parte. */
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [pedidos, setPedidos] = useState<DomicilioEnTableroVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [minutosLimite, setMinutosLimite] = useState(15);
  const [datosDePago, setDatosDePago] = useState<string | null>(null);
  const [cotizando, setCotizando] = useState<DomicilioEnTableroVista | null>(null);
  const [cobrando, setCobrando] = useState<DomicilioEnTableroVista | null>(null);
  /**
   * Sprint 99 — El remate del pedido, sin salir del tablero.
   *
   * `cobroEfectivo` es el cobro de mostrador (`CobroModal`), distinto del cobro por transferencia:
   * un pedido que se entregó y se paga en la puerta no pasa por comprobante.
   */
  const [entregandoALaMoto, setEntregandoALaMoto] = useState<PedidoVista | null>(null);
  const [noEntregado, setNoEntregado] = useState<PedidoVista | null>(null);
  const [cobroEfectivo, setCobroEfectivo] = useState<number | null>(null);
  const [copiadoId, setCopiadoId] = useState<number | null>(null);
  /**
   * Tick de reloj propio.
   *
   * Los minutos se recalculan en la pantalla con el mismo módulo puro que usa el servidor, en vez
   * de esperar al siguiente refresco: un reloj que sólo avanza cada 15 segundos se ve congelado
   * justo cuando alguien lo está mirando para decidir si llama al cliente.
   */
  const [ahora, setAhora] = useState(() => new Date());

  /**
   * Sprint 101 — El botón de cocina sólo se ofrece a quien el servidor va a dejar usarlo.
   *
   * Cortesía de interfaz, no control de acceso: quien autoriza es `safeHandleConPermiso`. Esto
   * evita el camino que el Sprint 64 llamó por su nombre — descubrir que no puedes DESPUÉS de
   * apretar.
   */
  const ajustesPermisos = useAjustesPermisos((sx) => sx.ajustes);
  const puedeCerrarCocina = puedeUsarCanal(sesion?.rol, 'pedidos:marcar-listo', ajustesPermisos);

  /**
   * Sprint 119 — Los que ya salieron: un número, no una columna.
   *
   * Se pide en el mismo ciclo que el tablero —sin temporizador aparte— porque es el mismo momento:
   * cada vez que la pantalla se refresca, el contador va con ella.
   */
  const [entregadosHoy, setEntregadosHoy] = useState(0);

  const cargar = useCallback(async () => {
    const [res, ent] = await Promise.all([
      window.api.pedidos.tableroDomicilios(),
      window.api.pedidos.domiciliosEntregadosHoy(),
    ]);
    if (res.ok) setPedidos(res.data);
    if (ent.ok) setEntregadosHoy(ent.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 15000);
    return () => clearInterval(t);
  }, [cargar]);

  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      const [min, pago] = await Promise.all([
        window.api.config.obtener(CLAVE_MINUTOS_ESPERA_PAGO),
        window.api.config.obtener(CLAVE_DATOS_PAGO),
      ]);
      if (min.ok) setMinutosLimite(leerMinutosEspera(min.data));
      // Si el rol no alcanza los datos bancarios, el canal rebota: no es un error que mostrar.
      if (pago.ok) setDatosDePago(pago.data);
    })();
  }, []);

  const porColumna = useMemo(() => {
    const mapa = new Map<string, DomicilioEnTableroVista[]>();
    for (const col of COLUMNAS_TABLERO) mapa.set(col.clave, []);
    for (const p of pedidos) {
      const col = COLUMNAS_TABLERO.find((c) => c.motivos.includes(p.motivo as MotivoEspera));
      if (col) mapa.get(col.clave)!.push(p);
    }
    return mapa;
  }, [pedidos]);

  const copiar = async (id: number, texto: string) => {
    const ok = await copiarAlPortapapeles(texto);
    setCopiadoId(ok ? id : null);
    if (!ok) {
      await window.alertar({
        titulo: 'No se pudo copiar',
        mensaje: 'Copia el mensaje a mano desde el detalle del pedido.',
        variant: 'warn',
      });
    }
  };

  const prorrogar = async (p: DomicilioEnTableroVista) => {
    const res = await window.api.pedidos.prorrogarPago({
      pedidoId: p.id,
      minutos: minutosLimite,
    });
    if (!res.ok) {
      await window.alertar({
        titulo: 'No se pudo esperar más',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  const cancelar = async (p: DomicilioEnTableroVista) => {
    if (!sesion) return;
    const confirmado = await window.confirmar({
      titulo: `Cancelar el pedido #${p.numeroPedido}`,
      mensaje:
        'Nada entró a cocina, así que cancelarlo no cuesta comida. El pedido queda registrado como cancelado.',
      textoConfirmar: 'Cancelar el pedido',
      variant: 'destructive',
    });
    if (!confirmado) return;
    const res = await window.api.pedidos.cancelar(
      p.id,
      'No mandó el comprobante de la transferencia',
      sesion.usuarioId,
    );
    if (!res.ok) {
      await window.alertar({
        titulo: 'No se pudo cancelar',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  /**
   * Sprint 99 — "Ya lo entregó".
   *
   * Si el pedido no estaba pagado, el servidor lo deja en `entregado` y hay que cobrarlo: se abre
   * el cobro en el momento, que es lo que pasa en la vida real cuando el repartidor vuelve con el
   * dinero. Si ya estaba pagado por transferencia, no se ofrece cobrar nada (Sprint 88).
   */
  const marcarEntregado = async (p: DomicilioEnTableroVista) => {
    const r = await window.api.pedidos.marcarEntregado({ pedidoId: p.id });
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo marcar como entregado',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
    if (r.data.estado === 'entregado') setCobroEfectivo(p.id);
  };

  /**
   * Sprint 100 — Cerrar un pedido que se entregó y ya estaba pagado.
   *
   * No cobra: pone el estado de acuerdo con el pago que ya existía. Las dos condiciones las
   * comprueba el servidor; aquí sólo se ofrece cuando se cumplen, para no enseñar un botón que
   * vaya a rebotar.
   */
  const cerrarYaPagado = async (p: DomicilioEnTableroVista) => {
    const r = await window.api.pedidos.cerrarEntregadoPagado({ pedidoId: p.id });
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo cerrar el pedido',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  /**
   * Sprint 101 — "Ya está listo y empaquetado", desde el tablero.
   *
   * Son dos hitos del sistema —`marcar-listo` (salió de la parrilla) y `marcar-empaquetado` (está
   * en su bolsa)— y para quien cocina y empaqueta sola son **un solo momento**. Pedirle dos toques
   * sería modelar nuestra base de datos, no su trabajo.
   *
   * Si el segundo falla, el primero ya quedó: el pedido igual avanza a "Listo · esperando moto",
   * que es lo que importa. Por eso no se anuncia un error si sólo falta la hora de empaquetado.
   */
  /**
   * Sprint 137 — Mandarlo a la parrilla cuando se acordó cobrarlo al entregar.
   *
   * Es la salida del único callejón que abre el contra entrega: el pedido se guardó con el
   * acuerdo pero la comanda no llegó a salir (se cerró el navegador, falló la impresión). Sin
   * este botón quedaría vivo en el tablero sin nada que hacerle — y "Subir su comprobante" no
   * sirve, porque no va a haber comprobante nunca.
   */
  const mandarACocina = async (p: DomicilioEnTableroVista) => {
    const res = await window.api.pedidos.enviarACocina(p.id);
    if (!res.ok) {
      await window.alertar({
        titulo: 'No se pudo mandar a cocina',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  const marcarListoYEmpaquetado = async (p: DomicilioEnTableroVista) => {
    const listo = await window.api.pedidos.marcarListo(p.id);
    if (!listo.ok) {
      await window.alertar({
        titulo: 'No se pudo marcar como listo',
        mensaje: traducirErrorBackend(listo.error, listo.codigo),
        variant: 'warn',
      });
      return;
    }
    await window.api.pedidos.marcarEmpaquetado(p.id);
    void cargar();
  };

  const totalVivo = pedidos.reduce((acc, p) => acc + p.total, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Bike className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">A domicilio</h1>
            <p className="text-sm text-slate-500">
              {pedidos.length === 0
                ? 'Sin pedidos a domicilio en curso.'
                : `${pedidos.length} en curso · ${formatPrecio(totalVivo, monedaSimbolo, locale)}`}
              {/*
                El día no se borra al vaciarse el tablero: aunque no quede nada en curso, saber
                que salieron doce es la diferencia entre "no hay nada" y "ya está todo entregado".
              */}
              {entregadosHoy > 0 && (
                <span className="ml-1 text-emerald-700">
                  {pedidos.length > 0 ? ' · ' : ''}
                  <strong>{entregadosHoy}</strong> {entregadosHoy === 1 ? 'entregado' : 'entregados'} hoy
                </span>
              )}
            </p>
          </div>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost" aria-label="Refrescar">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/*
        Sprint 95 — Por dónde empieza un pedido.

        Estaba fuera del tablero porque el tablero modela **pedidos vivos**, y en el primer paso
        todavía no hay pedido: el cliente sólo preguntó "¿qué tienes?". Ese hueco dejaba los dos
        primeros pasos de los dos flujos sin casa — el enlace vivía en Configuración y el
        intérprete del chat, tres pasos adentro del asistente del POS.

        "Nuevo pedido a domicilio" dejó de ser un botón suelto en la esquina y es la tercera
        tarjeta: son tres formas de empezar lo mismo y ninguna es más importante que las otras.
      */}
      {/*
        Sprint 98 — Ya no avisa "empecé un pedido": las tres tarjetas navegan al POS y el pedido
        nace allá. Al volver, el tablero se recarga por su cuenta.
      */}
      <ComoEmpiezaElPedido />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COLUMNAS_TABLERO.map((col) => {
          const lista = porColumna.get(col.clave) ?? [];
          return (
            <section key={col.clave} className="card overflow-hidden p-0">
              <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
                <h2 className="text-sm font-semibold text-slate-700">{col.titulo}</h2>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">
                  {lista.length}
                </span>
              </header>
              <div className="space-y-3 p-3">
                {cargando ? (
                  <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>
                ) : lista.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">Nada por aquí.</p>
                ) : (
                  lista.map((p) => (
                    <TarjetaDomicilio
                      key={p.id}
                      pedido={p}
                      ahora={ahora}
                      minutosLimite={minutosLimite}
                      monedaSimbolo={monedaSimbolo}
                      locale={locale}
                      copiado={copiadoId === p.id}
                      onCotizar={() => setCotizando(p)}
                      onCobrar={() => setCobrando(p)}
                      onProrrogar={() => void prorrogar(p)}
                      onCancelar={() => void cancelar(p)}
                      onEntregarALaMoto={() => setEntregandoALaMoto(p as unknown as PedidoVista)}
                      onMarcarEntregado={() => void marcarEntregado(p)}
                      onCobrarEnMostrador={() => setCobroEfectivo(p.id)}
                      onCerrar={() => void cerrarYaPagado(p)}
                      onYaEstaListo={() => void marcarListoYEmpaquetado(p)}
                      onMandarACocina={() => void mandarACocina(p)}
                      puedeCerrarCocina={puedeCerrarCocina}
                      onNoEntregado={() => setNoEntregado(p as unknown as PedidoVista)}
                      /* Cancela el pedido y restituye stock: sólo el dueño, igual que antes. */
                      puedeMarcarNoEntregado={sesion?.rol === 'dueño'}
                      onCopiarPago={() =>
                        void copiar(
                          p.id,
                          mensajeDatosParaPagar({
                            folio: p.numeroPedido,
                            clienteNombre: p.clienteNombre,
                            totalATransferir: p.total,
                            datosDePago,
                            simboloMoneda: monedaSimbolo,
                          }),
                        )
                      }
                      onCopiarRecordatorio={() =>
                        void copiar(
                          p.id,
                          mensajeRecordatorioPago({
                            clienteNombre: p.clienteNombre,
                            totalATransferir: p.total,
                            simboloMoneda: monedaSimbolo,
                          }),
                        )
                      }
                      onCopiarAcuse={() =>
                        void copiar(p.id, mensajeAcuse({ clienteNombre: p.clienteNombre }))
                      }
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>

      <DevolucionesPendientes recargar={cargar} monedaSimbolo={monedaSimbolo} locale={locale} />

      <CotizarEnvioModal
        pedido={cotizando}
        nombreNegocio={negocio?.nombre}
        monedaSimbolo={monedaSimbolo}
        onCerrar={() => setCotizando(null)}
        onGuardado={(pedido) => {
          setCotizando(null);
          void cargar();
          /**
           * Cotizado ⇒ lo siguiente es cobrarle. Se abre el cobro en el momento porque es
           * exactamente lo que sigue en la conversación con el cliente: "son $X, aquí está la
           * cuenta". Obligar a buscar el pedido otra vez en el tablero sería un paso de más.
           */
          setCobrando({ ...(cotizando as DomicilioEnTableroVista), ...pedido });
        }}
      />

      <CobroTransferenciaModal
        abierto={cobrando !== null}
        pedido={cobrando}
        onClose={() => setCobrando(null)}
        onConfirmado={() => {
          setCobrando(null);
          void cargar();
        }}
      />

      {/*
        Sprint 99 — Lo que antes vivía en la pantalla de Despacho.

        Los tres modales son los mismos, con las mismas reglas: entregar imprime la ficha del
        repartidor, "no entregado" exige PIN del dueño y motivo, y el cobro de mostrador es el de
        siempre. Lo único que cambió es que ya no hay que mudarse de pantalla para llegar a ellos.
      */}
      <AsignarRepartidorModal
        pedido={entregandoALaMoto}
        onClose={() => setEntregandoALaMoto(null)}
        onAsignado={(pedido, ruta) => {
          setEntregandoALaMoto(null);
          /*
            Sprint 56.1 — En web el PDF vive en el servidor y `abrirPdf` (shell.openPath) no puede
            abrirlo en la máquina del cajero: la orden de entrega se imprime desde el navegador.
          */
          if (esWeb()) {
            void imprimirDocumento({ tipo: 'despacho', pedidoId: pedido.id });
          } else if (ruta) {
            void window.api.ticket.abrirPdf(ruta);
          }
          void cargar();
        }}
      />

      <NoEntregadoModal
        pedido={noEntregado}
        onClose={() => setNoEntregado(null)}
        onConfirmado={() => {
          setNoEntregado(null);
          void cargar();
        }}
      />

      <CobroModal
        abierto={cobroEfectivo !== null}
        pedidoId={cobroEfectivo}
        onClose={() => setCobroEfectivo(null)}
        onCobrado={() => {
          setCobroEfectivo(null);
          void cargar();
        }}
      />
    </div>
  );
}

/**
 * Sprint 77 (DT-072) — El dinero de los pedidos que no se entregaron.
 *
 * Hasta ahora, un domicilio pagado por transferencia que no llegó quedaba `cancelado` y los
 * reportes excluyen los cancelados: el dinero real seguía en la cuenta del banco sin aparecer en
 * ningún lado, ni cuando se devolvía. La auditoría del Sprint 69 lo llamó *"dinero cobrado y no
 * entregado que el sistema no sabe nombrar"*.
 *
 * Este panel sólo existe cuando hay algo que devolver, y no se puede ignorar sin resolverlo: es
 * dinero de un cliente que el negocio tiene en su cuenta.
 */
function DevolucionesPendientes({
  recargar,
  monedaSimbolo,
  locale,
}: {
  recargar: () => Promise<void> | void;
  monedaSimbolo: string;
  locale: string;
}) {
  const [pendientes, setPendientes] = useState<DevolucionPendienteVista[]>([]);
  const [resolviendo, setResolviendo] = useState<number | null>(null);
  const [como, setComo] = useState('');

  const cargar = useCallback(async () => {
    const res = await window.api.pedidos.devolucionesPendientes();
    // Si el rol no alcanza el canal (es del dueño), simplemente no hay panel que mostrar.
    if (res.ok) setPendientes(res.data);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (pendientes.length === 0) return null;

  const registrar = async (d: DevolucionPendienteVista) => {
    const res = await window.api.pedidos.registrarDevolucion({
      pedidoId: d.pedidoId,
      justificacion: como.trim(),
    });
    if (!res.ok) {
      await window.alertar({
        titulo: 'No se pudo registrar',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    setResolviendo(null);
    setComo('');
    await cargar();
    await recargar();
  };

  const total = pendientes.reduce((acc, d) => acc + d.monto, 0);

  return (
    <section className="card overflow-hidden border-rose-300 p-0">
      <header className="flex items-center justify-between gap-2 border-b border-rose-300 bg-rose-50 px-4 py-2">
        <h2 className="text-sm font-semibold text-rose-800">
          Dinero por devolver — pedidos cobrados que no se entregaron
        </h2>
        <span className="font-mono text-sm font-bold text-rose-800">
          {formatPrecio(total, monedaSimbolo, locale)}
        </span>
      </header>
      <div className="divide-y divide-slate-100">
        {pendientes.map((d) => (
          <div key={d.pedidoId} className="space-y-2 px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="font-mono font-bold">
                  #{String(d.numeroPedido).padStart(5, '0')}
                </span>
                <span className="ml-2 text-sm text-slate-700">
                  {d.clienteNombre ?? 'Sin nombre'}
                </span>
                {d.clienteTelefono && (
                  <span className="ml-2 text-xs text-slate-500">☎ {d.clienteTelefono}</span>
                )}
              </div>
              <span className="font-mono font-bold text-rose-700">
                {formatPrecio(d.monto, monedaSimbolo, locale)}
              </span>
            </div>
            {d.motivoNoEntrega && (
              <p className="text-xs text-slate-500">No entregado: {d.motivoNoEntrega}</p>
            )}

            {resolviendo === d.pedidoId ? (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[220px] flex-1">
                  <label className="label" htmlFor={`como-${d.pedidoId}`}>
                    ¿Cómo se le devolvió?
                  </label>
                  <input
                    id={`como-${d.pedidoId}`}
                    className="input"
                    value={como}
                    onChange={(e) => setComo(e.target.value)}
                    placeholder="Transferencia de vuelta, efectivo en el local…"
                    autoFocus
                  />
                </div>
                <button
                  onClick={() => void registrar(d)}
                  className="btn-primary min-h-[44px]"
                  disabled={como.trim().length < 5}
                >
                  Registrar la devolución
                </button>
                <button onClick={() => setResolviendo(null)} className="btn-ghost">
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setResolviendo(d.pedidoId);
                  setComo('');
                }}
                className="btn-ghost min-h-[44px] border border-rose-200 text-sm text-rose-700"
              >
                Ya le devolví su dinero
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
        Al registrarlo se anota una salida de caja con el número de pedido, para que el corte
        cuadre y quede claro a dónde fue ese dinero.
      </p>
    </section>
  );
}

/** Una tarjeta del tablero: qué espera, desde cuándo, y **una** acción. */
function TarjetaDomicilio(props: {
  pedido: DomicilioEnTableroVista;
  ahora: Date;
  minutosLimite: number;
  monedaSimbolo: string;
  locale: string;
  copiado: boolean;
  onCotizar: () => void;
  onCobrar: () => void;
  onProrrogar: () => void;
  onCancelar: () => void;
  /**
   * Sprint 99 — Las tres acciones que antes obligaban a cambiar de pantalla.
   *
   * El tablero modelaba el ciclo completo pero no podía cerrarlo: "Entregar a la moto", "Ver en
   * despacho" y "Cobrar en despacho" navegaban a `/despacho`, y ahí el operador perdía de vista
   * las otras columnas —cuántos hay en cocina, quién sigue esperando pago— justo con el
   * repartidor en la puerta.
   */
  onEntregarALaMoto: () => void;
  /** Cobro de mostrador: el repartidor volvió con el dinero. No es el de transferencia. */
  onCobrarEnMostrador: () => void;
  /** Sprint 100 — Entregado y ya pagado: no falta dinero, falta cerrarlo. */
  onCerrar: () => void;
  /** Sprint 101 — Salió de la parrilla y quedó empaquetado, dicho desde aquí. */
  onYaEstaListo: () => void;
  /** Sprint 137 — Contra entrega que se quedó sin salir a la parrilla. */
  onMandarACocina: () => void;
  puedeCerrarCocina: boolean;
  onMarcarEntregado: () => void;
  onNoEntregado: () => void;
  puedeMarcarNoEntregado: boolean;
  onCopiarPago: () => void;
  onCopiarRecordatorio: () => void;
  onCopiarAcuse: () => void;
}) {
  const { pedido: p, ahora, minutosLimite, monedaSimbolo, locale, copiado } = props;
  const motivo = p.motivo as MotivoEspera;
  const minutos = minutosEsperando(
    { ...p, fechaCreacion: p.fechaCreacion },
    ahora,
  );
  const vencida = esperaDePagoVencida(p, ahora, minutosLimite);
  /**
   * Sprint 100 — La regla del Sprint 88, que faltaba aquí.
   *
   * `seLePuedeCobrar` vive en `shared` justamente para que ninguna pantalla decida esto por su
   * cuenta, y el Sprint 99 trajo el botón de cobro al tablero **sin consultarla**. El resultado
   * fue un botón "Cobrar" sobre pedidos ya pagados: el servidor los rechazaba y no quedaba ninguna
   * salida. Un botón que dice "cobrar" sobre algo pagado invita a pedirle otra vez al cliente.
   */
  const yaEstaPagado = !seLePuedeCobrar(p);
  /**
   * Sprint 141 — Cuánto se cobra de verdad en la puerta.
   *
   * La misma función que arma el cartel de la ficha impresa, para que el papel que se lleva la
   * moto y la tarjeta que mira quien despacha no puedan decir cifras distintas del mismo pedido.
   */
  const cobroPuerta = calcularCobroEnLaPuerta({
    total: p.total,
    costoEnvio: p.costoEnvio ?? 0,
    envioPagaCliente: p.envioPagaCliente ?? false,
    pagadoRegistrado: p.pagadoRegistrado ?? 0,
    tienePagos: (p.pagadoRegistrado ?? 0) > 0,
    estadoPago: p.estadoPago,
    pagaAlEntregar: p.pagaAlEntregar,
    pagaCon: p.pagaCon,
  });

  return (
    <article
      className={`rounded-xl border p-3 ${
        vencida ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-base font-bold">
          #{String(p.numeroPedido).padStart(5, '0')}
        </span>
        <span className="font-mono text-base font-bold">
          {formatPrecio(p.total, monedaSimbolo, locale)}
        </span>
      </div>
      <div className="mt-0.5 text-sm text-slate-700">{p.clienteNombre ?? 'Sin nombre'}</div>
      {p.clienteDireccion && (
        <div className="mt-1 flex items-start gap-1 text-xs text-slate-500">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">{p.clienteDireccion}</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            vencida ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {vencida
            ? `Pasó de ${minutosLimite} min sin pagar`
            : /*
                Sprint 100 — "Entregado · falta cobrar" sobre un pedido pagado es una mentira.

                El motivo se calcula por `estado`, y un pedido que se entregó y **después** se pagó
                se queda en `entregado`: el tablero pedía cobrarlo y el servidor contestaba "ya
                está pagado". Aquí se dice lo que hay, y abajo se ofrece la única acción que falta.
              */
              yaEstaPagado && motivo === 'entregado_falta_cobro'
              ? 'Entregado · ya pagado'
              : ETIQUETA_MOTIVO[motivo]}
        </span>
        <span
          className={`font-mono text-xs ${vencida ? 'font-bold text-rose-600' : 'text-slate-500'}`}
        >
          ⏱ {minutos} min
        </span>
      </div>

      {/*
        El historial del cliente sólo se enseña cuando hay una decisión que tomar con él. El
        negocio lo dijo así: "si el cliente es conocido lo consideramos". Sin el dato, considerar
        a alguien a las tres de la tarde es adivinar.
      */}
      {vencida && p.pedidosPreviosCliente > 0 && (
        <p className="mt-2 rounded bg-white/70 px-2 py-1 text-xs text-emerald-800">
          Ya te ha pedido <b>{p.pedidosPreviosCliente}</b>{' '}
          {p.pedidosPreviosCliente === 1 ? 'vez' : 'veces'}.
        </p>
      )}

      {/*
        Sprint 137 — Lo que hay que cobrar en la puerta, visible en toda la vida del pedido.

        Va en la tarjeta y no sólo en la ficha impresa porque quien atiende contesta el teléfono
        con el tablero delante: si el cliente llama para preguntar cuánto va a pagar, la
        respuesta tiene que estar a la vista, no en un papel que ya se fue con la moto.
      */}
      {/*
        Sprint 141 — El acuerdo deja de anunciarse en cuanto se cumple.

        `pagaAlEntregar` no se borra al cobrar —es el registro de que hubo trato, y quién lo
        firmó—, así que un pedido ya saldado seguía mostrando el aviso: *"Se cobra en la puerta:
        $0.00"*. Un cartel que pide cobrar cero es peor que no tener cartel.
      */}
      {p.pagaAlEntregar && !cobroPuerta.estaCubierto && motivo !== 'entregado_falta_cobro' && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {/*
            Sprint 141 — El monto sale de la misma cuenta que la ficha impresa.

            Aquí decía `p.total`, y era cierto mientras un domicilio sólo pudiera estar pagado o no
            pagado. Desde que al que ya pagó se le puede agregar comida, ese número manda a cobrar
            de más: el pedido de prueba tenía $195 transferidos y la tarjeta pedía $340.
          */}
          <div className="font-bold">
            Se cobra en la puerta: {formatPrecio(cobroPuerta.falta, monedaSimbolo, locale)}
          </div>
          {cobroPuerta.pagoParcial && (
            <div>
              Ya pagó {formatPrecio(cobroPuerta.yaPago, monedaSimbolo, locale)} de{' '}
              {formatPrecio(p.total, monedaSimbolo, locale)}
            </div>
          )}
          {cobroPuerta.cambio && (
            <div>
              Paga con {formatPrecio(cobroPuerta.cambio.pagaCon, monedaSimbolo, locale)} · llevar{' '}
              {formatPrecio(cobroPuerta.cambio.cambio, monedaSimbolo, locale)} de cambio
            </div>
          )}
          {/*
            Sprint 137 — A nombre de quién salió esta comida.

            Se guardaba desde el primer día y no se veía en ninguna parte. Un registro que nadie
            puede leer no sirve para la conversación del día siguiente, que es justo para lo que
            se guarda.
          */}
          {p.pagaAlEntregarPorNombre && (
            <div className="mt-0.5 text-amber-800">
              Lo autorizó <b>{p.pagaAlEntregarPorNombre}</b>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {/*
          Sprint 137 — El acuerdo está tomado y la comida no ha salido. Una sola acción.

          No se ofrece "subir comprobante" ni se cuenta el reloj: en este pedido no va a llegar
          ninguna captura, y ofrecerla sería invitar a pedirle al cliente un pago que ya se
          acordó cobrar después.
        */}
        {motivo === 'contra_entrega_falta_cocina' && (
          <>
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Se acordó cobrarlo al entregar. <b>Todavía no ha salido a cocina.</b>
            </p>
            <button
              onClick={props.onMandarACocina}
              className="btn-primary min-h-[44px] w-full bg-amber-600 text-sm hover:bg-amber-700"
            >
              <ChefHat className="h-4 w-4" />
              Mandarlo a cocina
            </button>
            <button
              onClick={props.onCancelar}
              className="btn-ghost min-h-[44px] w-full justify-center border border-rose-200 text-xs text-rose-700"
            >
              Cancelarlo
            </button>
          </>
        )}

        {/*
          Sprint 92 — Lo que llegó del enlace. La acción es **la misma que cotizar**, y es a
          propósito: revisar el pedido y preguntarle al grupo cuánto cobran son el mismo momento.
          Lo que cambia es el aviso — este pedido no lo tomó nadie del negocio, así que el primer
          trabajo es mirarlo.
        */}
        {motivo === 'llego_del_enlace' && (
          <>
            <p className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
              Lo armó el cliente desde tu enlace. <b>Nadie lo ha revisado.</b> No descontó
              inventario ni entró a cocina.
            </p>
            <button onClick={props.onCotizar} className="btn-primary min-h-[44px] w-full text-sm">
              <Bike className="h-4 w-4" />
              Revisar y cotizar el envío
            </button>
            <button
              onClick={props.onCancelar}
              className="btn-ghost min-h-[44px] w-full justify-center border border-rose-200 text-xs text-rose-700"
            >
              No es un pedido real · descartarlo
            </button>
          </>
        )}

        {motivo === 'falta_cotizar_envio' && (
          <button onClick={props.onCotizar} className="btn-primary min-h-[44px] w-full text-sm">
            <Bike className="h-4 w-4" />
            Cotizar el envío
          </button>
        )}

        {/*
          Sprint 101 — "Dice que ya transfirió".

          Hueco que encontró la dueña probando el flujo: el cliente depositaba y no tenía forma de
          avisar desde su página. Ahora sí, y aquí es donde eso vale: entre todos los que están sin
          comprobante, éste es al que hay que buscarle el depósito en la cuenta.

          **No dice "pagado" en ningún lado**, y es a propósito: lo escribió el cliente, no el
          banco. Lo confirma quien mira el estado de cuenta, con el botón de abajo.
        */}
        {motivo === 'falta_comprobante' && p.avisoPagoCliente && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
            <span className="font-bold">El cliente dice que ya transfirió</span>
            <span className="block text-emerald-800">
              Lo avisó {minutosDesde(p.avisoPagoCliente, ahora)} · búscalo en tu cuenta y confírmalo
            </span>
          </div>
        )}

        {motivo === 'falta_comprobante' && !vencida && (
          <>
            <button onClick={props.onCobrar} className="btn-primary min-h-[44px] w-full text-sm">
              <Upload className="h-4 w-4" />
              Subir su comprobante
            </button>
            <button
              onClick={props.onCopiarPago}
              className="btn-ghost min-h-[44px] w-full justify-center border border-slate-300 text-xs"
            >
              <Copy className="h-3.5 w-3.5" />
              {copiado ? '✓ Copiado' : 'Copiar datos para transferir'}
            </button>
          </>
        )}

        {/*
          Sprint 76 — El plazo se cumplió: el sistema **pregunta**, no cancela.

          El negocio fijó 15 minutos y en la misma frase dijo que a un cliente conocido se le
          considera. Un sistema que cancelara por reloj le quitaría esa decisión, y el día que
          tirara el pedido de un cliente bueno nadie volvería a confiar en el tablero.
        */}
        {motivo === 'falta_comprobante' && vencida && (
          <>
            <button onClick={props.onProrrogar} className="btn-primary min-h-[44px] w-full text-sm">
              <Clock className="h-4 w-4" />
              Esperarlo {minutosLimite} minutos más
            </button>
            <button
              onClick={props.onCopiarRecordatorio}
              className="btn-ghost min-h-[44px] w-full justify-center border border-slate-300 text-xs"
            >
              <Copy className="h-3.5 w-3.5" />
              {copiado ? '✓ Copiado' : 'Copiar recordatorio'}
            </button>
            <div className="flex gap-2">
              <button onClick={props.onCobrar} className="btn-ghost flex-1 border border-slate-300 text-xs">
                Ya llegó su pago
              </button>
              <button
                onClick={props.onCancelar}
                className="btn-ghost flex-1 border border-rose-200 text-xs text-rose-700"
              >
                Cancelarlo
              </button>
            </div>
          </>
        )}

        {/*
          Sprint 101 — Cerrar la cocina sin ir a Cocina.

          Petición del negocio: *"Claudia está sola y hace todo, y tiene la comida enfrente"*. Para
          decir que un pedido ya salió de la parrilla tenía que cambiar de sección —perdiendo de
          vista el tablero entero— y volver. Con una persona atendiendo, ese viaje de ida y vuelta
          se paga en pedidos que nadie está mirando.

          No es una función nueva ni un permiso nuevo: son los mismos canales que usa la pantalla de
          Cocina (`marcar-listo` y `marcar-empaquetado`), ofrecidos donde ella ya está parada. Quien
          no tenga el permiso no ve el botón, y el servidor sigue decidiendo.
        */}
        {motivo === 'en_cocina' && (
          <>
            {/*
              Sprint 141 — "Pagado y en cocina" con dinero pendiente es una contradicción, y estaba
              en la misma tarjeta que el aviso de cobro: arriba decía cuánto cobrar en la puerta y
              dos líneas abajo, que ya estaba pagado. Quien lee rápido se queda con la de abajo.
            */}
            <p className="flex items-center gap-2 rounded bg-slate-50 px-2 py-2 text-xs text-slate-600">
              <ChefHat className="h-4 w-4 shrink-0" />
              {cobroPuerta.estaCubierto
                ? 'Pagado y en cocina.'
                : `En cocina · falta cobrar ${formatPrecio(cobroPuerta.falta, monedaSimbolo, locale)}.`}
            </p>
            {props.puedeCerrarCocina && (
              <button
                onClick={props.onYaEstaListo}
                className="btn-primary min-h-[44px] w-full justify-center text-sm"
              >
                <Check className="h-4 w-4" />
                Ya está listo y empaquetado
              </button>
            )}
          </>
        )}

        {/*
          Sprint 99 — El pedido se cierra aquí.

          Éstas son las acciones que vivían en la pantalla de Despacho. Bajaron a la tarjeta porque
          el tablero ya es la fuente única del domicilio: mandar a otra pantalla para rematar era
          la confesión de que no lo era del todo.
        */}
        {motivo === 'listo_esperando_moto' && (
          <>
            <button
              onClick={props.onEntregarALaMoto}
              className="btn-primary min-h-[44px] w-full justify-center bg-emerald-600 text-sm hover:bg-emerald-700"
            >
              <Bike className="h-4 w-4" />
              {p.nombreRepartidor ? `Entregar a ${p.nombreRepartidor}` : 'Entregar a la moto'}
            </button>
            {p.nombreRepartidor && (
              <p className="text-center text-xs text-slate-500">
                Viene <b>{p.nombreRepartidor}</b> · se imprime su ficha al entregar
              </p>
            )}
          </>
        )}

        {motivo === 'en_ruta' && (
          <>
            <button
              onClick={props.onMarcarEntregado}
              className="btn-primary min-h-[44px] w-full justify-center text-sm"
            >
              <Check className="h-4 w-4" />
              Ya lo entregó
            </button>
            {p.nombreRepartidor && (
              <p className="text-center text-xs text-slate-500">
                Va con <b>{p.nombreRepartidor}</b>
              </p>
            )}
            <button
              onClick={props.onCopiarAcuse}
              className="btn-ghost min-h-[44px] w-full justify-center border border-slate-300 text-xs"
            >
              <Copy className="h-3.5 w-3.5" />
              {copiado ? '✓ Copiado' : '¿Ya te llegó?'}
            </button>
            {/*
              "No entregado" cancela el pedido y restituye stock: sigue siendo sólo del dueño y con
              PIN. Va en gris y al final, como lo que es — la salida excepcional, no la normal.
            */}
            {props.puedeMarcarNoEntregado && (
              <button
                onClick={props.onNoEntregado}
                className="btn-ghost min-h-[44px] w-full justify-center border border-rose-200 text-xs text-rose-700"
              >
                <X className="h-3.5 w-3.5" />
                No se pudo entregar
              </button>
            )}
          </>
        )}

        {motivo === 'entregado_falta_cobro' &&
          (yaEstaPagado ? (
            <>
              <button
                onClick={props.onCerrar}
                className="btn-primary min-h-[44px] w-full justify-center text-sm"
              >
                <Check className="h-4 w-4" />
                Dar por cerrado
              </button>
              <p className="text-center text-xs text-slate-500">
                Se entregó y el pago ya está registrado. No se cobra nada: sólo se cierra.
              </p>
            </>
          ) : (
            /*
              Sprint 100 — Este cobro es el de mostrador (efectivo en la puerta), no el de
              transferencia. En el Sprint 99 quedó apuntando al modal de transferencia, que sobre
              un pedido ya pagado contestaba "ese pedido ya está pagado" y dejaba al operador sin
              salida. El de mostrador es el que usaba la pantalla de Despacho de la que vino.
            */
            <button
              onClick={props.onCobrarEnMostrador}
              className="btn-primary min-h-[44px] w-full text-sm"
            >
              Cobrar {formatPrecio(p.total, monedaSimbolo, locale)}
            </button>
          ))}
      </div>
    </article>
  );
}

/**
 * Sprint 101 — Hace cuánto avisó el cliente que transfirió.
 *
 * En minutos, como todo lo demás del tablero: lo que decide si alguien levanta el teléfono es el
 * reloj, no la hora exacta. Se redondea hacia abajo y nunca muestra negativos —un reloj de cliente
 * adelantado no puede hacer que el sistema diga tonterías.
 */
export function minutosDesde(iso: string, ahora: Date): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'hace un momento';
  const min = Math.max(0, Math.floor((ahora.getTime() - t) / 60000));
  if (min < 1) return 'hace menos de un minuto';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.floor(min / 60);
  return `hace ${horas} h`;
}
